import { useState } from "react";
import { toast } from "sonner";
import { Crown, Loader2, Check, X } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";

const field = "block font-grotesk text-[8px] uppercase tracking-[0.26em] text-[#8A8A82] mb-1";
const dark = "bg-black/60 border-white/15 text-[#F5F5F0] focus-visible:ring-[#D8B23E]";

function ApproveDialog({ req, api, onDone, onClose }) {
  const [note, setNote] = useState("");
  const [days, setDays] = useState(14);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/vcb/access-requests/${req.id}/approve`, { note, expires_in_days: Number(days) || 14 });
      toast.success("Invitation issued", { description: `Both PhonePe + Razorpay links sent to ${req.email}` });
      onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Approval failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="vcb-approve-dialog" className="bg-[#050505] border border-[#D8B23E]/60 text-[#F5F5F0] max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2"><Crown size={16} className="text-[#D8B23E]" /> Approve &amp; Send Invitation</DialogTitle>
          <DialogDescription className="text-[12px] font-body text-[#C9C9C2]">Generates PhonePe + Razorpay links and dispatches the premium VCB invitation e-mail.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <label className="block"><span className={field}>Partner</span><Input readOnly value={`${req.name || "—"} · ${req.email}`} data-testid="vcb-approve-partner" className={`${dark} opacity-80`} /></label>
          <label className="block"><span className={field}>Personal note from admin (optional)</span>
            <Textarea data-testid="vcb-approve-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={dark} placeholder="We reviewed your profile and are pleased to extend this exclusive invitation" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className={field}>Phase 1 fee (locked)</span><Input readOnly value="INR 25,000 + GST = INR 29,500" data-testid="vcb-approve-fee" className={`${dark} opacity-80`} /></label>
            <label className="block"><span className={field}>Payment link validity (days)</span><Input type="number" min={1} max={180} value={days} onChange={(e) => setDays(e.target.value)} data-testid="vcb-approve-days" className={dark} /></label>
          </div>
          <div className="text-[10.5px] text-[#8A8A82] font-body">Phase 2 — INR 53,000 + INR 9,540 GST = INR 62,540 — is included in the same e-mail and is payable only after Phase 1 clearance.</div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="vcb-approve-cancel" className="text-[#C9C9C2]">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="vcb-approve-confirm" className="bg-[#D8B23E] text-black hover:bg-[#E5C450] font-grotesk uppercase tracking-[0.2em] text-[10px]">{busy ? <Loader2 size={12} className="animate-spin" /> : "Confirm & Send Invitation"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeclineDialog({ req, api, onDone, onClose }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/vcb/access-requests/${req.id}/decline`, { note });
      toast.success("Request declined", { description: `Courteous notice sent to ${req.email}` });
      onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Decline failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="vcb-decline-dialog" className="bg-[#050505] border border-white/20 text-[#F5F5F0] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Decline Request</DialogTitle>
          <DialogDescription className="text-[12px] font-body text-[#C9C9C2]">{req.name || req.email} will receive: “Thank you for your interest in Vinci Core Banking. After careful review, we are unable to extend access at this time.”</DialogDescription>
        </DialogHeader>
        <label className="block"><span className={field}>Optional note</span><Textarea data-testid="vcb-decline-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={dark} /></label>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="vcb-decline-cancel" className="text-[#C9C9C2]">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="vcb-decline-confirm" variant="outline" className="border-[#E5484D]/60 text-[#E5484D] font-grotesk uppercase tracking-[0.2em] text-[10px]">{busy ? <Loader2 size={12} className="animate-spin" /> : "Decline & Notify"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const tone = { pending: "text-[#D8B23E]", approved: "text-[#2FB999]", declined: "text-[#E5484D]" };

export function AccessRequests({ api, requests = [], onChange }) {
  const [dlg, setDlg] = useState(null);
  return (
    <div className="rounded-sm border border-white/10 bg-[#0A0906]">
      <div className="px-3 py-2 font-grotesk text-[8px] uppercase tracking-[0.28em] text-[#8A8A82] border-b border-white/10">Access requests (Request Access gate)</div>
      <div className="divide-y divide-white/5" data-testid="vcb-access-requests">
        {requests.length === 0 ? <div className="py-5 px-3 text-[11px] italic text-[#8A8A82]">No requests lodged.</div> : requests.map((r) => (
          <div key={r.id} className="px-3 py-2.5 text-[12px]" data-testid={`vcb-access-request-${r.id}`}>
            <div className="flex justify-between gap-2 items-start">
              <div><span className="text-[#F5F5F0]">{r.name || r.email}</span><div className="text-[10.5px] text-[#8A8A82]">{r.email} · {r.monthly_volume || "—"}</div></div>
              <span className={`font-grotesk text-[8px] uppercase tracking-[0.22em] ${tone[r.status] || "text-[#C9C9C2]"}`} data-testid={`vcb-access-status-${r.id}`}>{r.status}</span>
            </div>
            {r.purpose && <div className="text-[11px] text-[#C9C9C2] italic mt-0.5">{r.purpose}</div>}
            {r.status === "pending" ? (
              <div className="flex gap-1.5 mt-2">
                <Button size="sm" data-testid={`vcb-approve-${r.id}`} onClick={() => setDlg({ kind: "approve", req: r })} className="h-7 bg-[#D8B23E] text-black hover:bg-[#E5C450] font-grotesk uppercase tracking-[0.2em] text-[9px]"><Check size={10} /> Approve &amp; Invite</Button>
                <Button size="sm" variant="outline" data-testid={`vcb-decline-${r.id}`} onClick={() => setDlg({ kind: "decline", req: r })} className="h-7 border-white/20 text-[#C9C9C2] font-grotesk uppercase tracking-[0.2em] text-[9px]"><X size={10} /> Decline</Button>
              </div>
            ) : (
              <div className="text-[9.5px] text-[#8A8A82] mt-1 font-grotesk uppercase tracking-[0.16em]">{r.status} · {(r.decided_at || "").slice(0, 10)} · {r.decided_by}</div>
            )}
          </div>))}
      </div>
      {dlg?.kind === "approve" && <ApproveDialog req={dlg.req} api={api} onDone={onChange} onClose={() => setDlg(null)} />}
      {dlg?.kind === "decline" && <DeclineDialog req={dlg.req} api={api} onDone={onChange} onClose={() => setDlg(null)} />}
    </div>
  );
}
